<template>
  <div class="paies-list-page">

    <!-- SUBHEADER -->
    <div class="paies-list-page__subheader">

      <!-- FILTERS -->
      <b-card
        class="paies-list-page__filters"
        body-class="d-flex justify-content-space-between align-items-flex-start gap-10 p-1"
      >

        <!-- Filters: first column -->
        <paies-list-filters
          :class="[
            isConsumerPending ? 'paies-list-page__filters--busy' : '',
            'd-flex flex-grow-1 flew-wrap flex-basis-0'
          ]"
          v-bind="{
            filters: context.filters,
            updateFiltersCallback: setFilters,
            loadSalarieOptionsCallback: loadSalarieOptions,
            searchProfessionsCallback: searchProfessions,
            resetFiltersCallback: resetFilters,
          }"
        />

        <!-- Filter: second column -->
        <ui-wad class="d-flex align-items-center gap-1.5">
          <ui-chip-field-management
            v-model="fields"
            :class="[
              isConsumerPending ? 'paies-list-page__filters--busy' : '',
            ]"
          />

          <layout-cta-sub
            component="ui-button"
            :variant="UI.VARIANT.PRIMARY"
          >
            <ui-icon name="add" />
            Créer une paie
          </layout-cta-sub>
        </ui-wad>

      </b-card>

    </div>

    <div class="paies-list-page__main">

      <!-- MENU -->
      <layout-nav-tab-menu
        v-model="selectedTabMenu"
        class="paies-list-page__tab-menu"
        :items="tabMenuItems"
        badge-placeholder
        badge-animation
      />

      <!-- TABLE -->
      <div class="paies-list-page__table-scroll">
        <ui-table-wrapper
          ref="table"
          :provider="provider"
          :fields="fields"
          :initial-sort-by="context.order"
          :initial-sort-desc="context.sort === 'desc'"
          primary-key="id"
          :tbody-tr-class="tbodyRowClass"
          :size="UI.SIZE.LARGE"
          :hide-header="fetchState.status.value !== 'success'"
          hover
          striped
          fixed
          @row-clicked="({ item }) => openPaieDetails(item)"
        >
          <template
            v-if="fetchState.status.value !== 'success' || providerCallCount === 0"
            #table-busy
          >
            <layout-placeholder-table-body
              :count="context.perPage"
              :size="UI.SIZE.SMALL"
              striped
              class="paies-list-page__placeholder"
            />
          </template>
          <template #empty>
            <div class="paies-list-page__empty">
              <ui-icon
                name="search_off"
                class="font-size-40px color-black mb-1"
              />
              <p class="font-weight-600 color-black mb-0.25 font-family-heading">
                Aucun résultat trouvé
              </p>
              <p class="mb-2">
                Nous n'avons trouvé aucun bulletin de paie correspondant à votre recherche
              </p>
              <ui-button
                v-if="hasAnyCriterionSet"
                :variant="UI.VARIANT.LIGHT"
                :size="UI.SIZE.SMALL"
                @click="resetFilters"
              >
                <ui-icon
                  name="replay"
                />&nbsp;Réinitialiser les filtres
              </ui-button>
            </div>
          </template>

          <template
            v-if="
              isHolisticSuggestionDisplayed ||
              isHolisticSelectionEnabled
            "
            #top-row="{ columns }"
          >
            <ui-table-top-row-selection
              :suggestion-displayed="isHolisticSuggestionDisplayed"
              :selection-displayed="isHolisticSelectionEnabled"
              :selected-count="bufferedSelectedItemsCount"
              :total-count="context.total"
              :colspan="columns"
              class="paies-list-page__row-selection"
              @unselect="resetSelection"
              @select="setHolisticSelection"
            />
          </template>

          <template #head(selection)>
            <ui-button-icon
              :status="
                providerCallCount === 0 ? UI.STATUS.MUTED : UI.STATUS.ENABLED
              "
              :size="UI.SIZE.SMALL"
              :overlay-size="UI.SIZE.LARGE"
              :class="checkboxAllIconClassname"
              :name="checkboxAllIconName"
              :theme="UI.ICON_THEME.FILLED"
              @click="toggleSelectAll"
            />
          </template>

          <template #cell(selection)="data">
            <ui-button-icon
              :size="UI.SIZE.SMALL"
              :overlay-size="UI.SIZE.LARGE"
              :variant="UI.VARIANT.DARK"
              :name="isItemSelected(data.item) ?
                'check_box' :
                'check_box_outline_blank'
              "
              :theme="UI.ICON_THEME.FILLED"
              :class="
                isItemSelected(data.item) ?
                  'color-primary' :
                  'color-gray-dark'
              "
              @click="toggleItem(data.item)"
            />
          </template>

          <template #cell(nom)="props">
            <ui-tooltip-wrapper
              v-if="props.item.salarie"
              :title="`${props.item.salarie.nom} ${props.item.salarie.prenom} — ${props.item.salarie.matricule}`"
              class="d-block min-w-0"
              placement="top-start"
              no-caret
              no-animation
              :offset="6"
              :delay="100"
            >
              <span class="d-block text-truncate">
                {{ props.item.salarie.nom }} {{ props.item.salarie.prenom }}
              </span>
            </ui-tooltip-wrapper>
          </template>

          <template #cell(type_contrat)="props">
            {{ printTypeContrat(props.item.contrat.type) }}
          </template>

          <template #cell(etat)="props">
            <div class="paies-list-page__etat__container">

              <ui-icon
                v-if="
                  selectedTabMenu === TAB_MENU.ALL &&
                  props.item.status_paie === PaieStatusFilter.DRAFTED
                "
                name="edit_note"
                data-tooltip="Brouillon"
              />

              <ui-icon
                v-if="
                  selectedTabMenu === TAB_MENU.ALL &&
                  props.item.status_paie === PaieStatusFilter.CALCULATED
                "
                name="calculate"
                data-tooltip="Paie"
              />

              <img
                v-if="props.item.transat"
                class="paies-list-page__etat__container__svg"
                :src="'/images/brand/LOGOS_GHS_RVB_GHS_TRANSAT_ICÔNE.svg'"
                data-tooltip="Publié sur Transat"
              >

              <ui-icon
                v-if="props.item.reglement"
                name="euro"
                data-tooltip="Paiement effectué"
              />

              <ui-icon
                v-if="props.item.specificite !== SpecificitePaie.NONE"
                name="warning"
                class="color-warning"
                :data-tooltip="
                  props.item.specificite === SpecificitePaie.ANNULANTE ?
                    'Paie annulante' :
                    'Paie annulée'
                "
              />
            </div>
          </template>

          <template #cell(actions)>
            <ui-wad class="paies-list-page__actions">
              <ui-button-icon
                name="more_vert"
                :variant="UI.VARIANT.DARK"
                :size="UI.SIZE.SMALL"
                :overlay-size="UI.SIZE.MEDIUM"
                square
                ripple
                @click.stop
              />
            </ui-wad>
          </template>

          <template
            v-if="isCumulVisible"
            #custom-foot="{ fields: footFields }"
          >
            <tr
              :class="['paies-list-page__cumul-row', { 'paies-list-page__cumul-row--loading': isCumulsLoading }]"
            >
            <!-- @XXX "bootstrap-vue-next" sorting feature also work on tfooter
              ↳ workaround: use td instead of th -->
              <td
                v-for="field in footFields"
                :key="field.key"
                :class="field.class"
              >
                <template v-if="field.key === 'selection'">
                  <ui-wad class="d-flex align-items-center gap-1">
                    <span class="paies-list-page__cumul-row__label">Cumul</span>
                    <ui-buffered-pending-icon
                      :is-pending="isCumulsLoading"
                      :idle-phase-duration="300"
                      :sustain-phase-duration="300"
                      :size="UI.SIZE.SMALL"
                      :type="UI.SPINNER_TYPE.BORDER"
                    />
                  </ui-wad>
                </template>
                <template v-if="field.key === 'heures'">
                  {{ formatCumul('heures') }}
                </template>
                <template v-if="field.key === 'brut'">
                  {{ formatCumul('brut') }}
                </template>
                <template v-if="field.key === 'net_a_payer'">
                  {{ formatCumul('net_a_payer') }}
                </template>
                <template v-if="field.key === 'net_avant_pas'">
                  {{ formatCumul('net_avant_pas') }}
                </template>
                <template v-if="field.key === 'cout_employeur'">
                  {{ formatCumul('cout_employeur') }}
                </template>
                <template v-if="field.key === 'jours'">
                  {{ formatCumul('jours') }}
                </template>
                <template v-if="field.key === 'cachets'">
                  {{ formatCumul('cachets') }}
                </template>
              </td>
            </tr>
          </template>
        </ui-table-wrapper>
      </div>

      <!-- FOOTER -->
      <div class="paies-list-page__footer">

        <span
          v-hide="context.total === 0"
          class="paies-list-page__footer__count"
        >
          <layout-misc-float :value="context.total" />
          {{ context.total <= 1 ? 'résultat' : 'résultats' }}
        </span>

        <ui-blank
          v-if="context.total === 0"
          class="paies-list-page__footer__blank"
        />

        <ui-table-pagination
          v-model="context.currentPage"
          v-hide="context.total < context.perPage || context.total === 0"
          :total-rows="context.total"
          :per-page="context.perPage"
        />

        <div class="paies-list-page__footer__per-page">
          <ui-form-select
            v-model="context.perPage"
            :size="UI.SIZE.SMALL"
            :options="perPageOptions"
            class="paies-list-page__footer__per-page__select"
          />
          <div class="paies-list-page__footer__per-page__text">
            par page
          </div>
        </div>

      </div
      >
    </div>

    <!-- CANDY BAR: action menu -->
    <ui-candy-bar
      class="paies-list-page__candy-bar"
      :model-value="hasAnyItemSelected"
      :aside-counter="{
        value: selectionCount,
        singular: 'sélectionnée',
        plural: 'sélectionnées',
      }"
      fit-workspace
      :offset="105"
      @clear="resetSelection"
    >
      <ui-button
        :variant="UI.VARIANT.PRIMARY"
        :size="UI.SIZE.SMALL"
        outline
      >
        <ui-icon name="edit" />
        Modifier
      </ui-button>
      <ui-button
        :variant="UI.VARIANT.PRIMARY"
        :size="UI.SIZE.SMALL"
        :status="canPreview ? UI.STATUS.ENABLED : UI.STATUS.DISABLED"
        outline
        @click="openPreview"
      >
        <ui-icon name="visibility" />
        Prévisualiser
      </ui-button>
      <ui-button
        :variant="UI.VARIANT.PRIMARY"
        :size="UI.SIZE.SMALL"
        outline
      >
        <ui-icon name="account_balance" />
        Générer fichier de virement
      </ui-button>
      <ui-button
        :variant="UI.VARIANT.PRIMARY"
        :size="UI.SIZE.SMALL"
        outline
      >
        <ui-icon name="delete" />
        Supprimer
      </ui-button>
    </ui-candy-bar>

    <!-- SIDEBAR: paie preview -->
    <ui-sidebar
      id="paies-preview-sidebar"
      v-model="displayPreviewSidebar"
      :size="UI.SIZE.MEDIUM"
      :title="previewFilename"
      placement="end"
      fit-workspace
      shadow
      lazy
      @hidden="onClosePreview"
    >
      <div
        v-if="isPreviewLoading"
        class="paies-list-page__preview-loader"
      >
        <ui-buffered-pending-icon
          :is-pending="isPreviewLoading"
          :idle-phase-duration="300"
          :sustain-phase-duration="300"
          :size="UI.SIZE.LARGE"
          :type="UI.SPINNER_TYPE.BORDER"
        />
      </div>
      <single-paie-viewer
        v-if="!isPreviewLoading && previewPdfUrl"
        :pdf-url="previewPdfUrl"
      />
    </ui-sidebar>

  </div>
</template>


<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/table/members'
@import '~/assets/sass/abstracts/members'
@import '~/assets/sass/modules/paie/members'

$actions-column-width: 34px
$selection-column-width: 50px
$code-societe-column-width: 90px
$numero-paie-column-width: 80px
$salarie-column-width: 210px
$profession-column-width: 250px
$date-column-width: 75px
$duree-column-width: 90px
$brut-column-width: 110px
$net-a-payer-column-width: 120px
$net-avant-pas-column-width: 130px
$cout-employeur-column-width: 150px
$jour-column-width: 70px
$heure-column-width: 70px
$cachets-column-width: 70px
$type-contrat-column-width: 65px
$code-contrat-column-width: 110px
$date-dsn-column-width: 120px
$reglement-column-width: 100px
$analytique-libelle-column-width: 120px
$analytique-code-column-width: 130px
$numero-objet-column-width: 120px
$etat-column-width: 110px
$derniere-modif-column-width: 130px
$date-reglement-column-width: 120px
$heures-column-width: 70px
$jours-column-width: 70px
$cumul-row-height: 37px

.paies-list-page
  $self: &

  height: 100%
  display: flex
  flex-direction: column

  &__subheader
    background-color: $subheader-bg
    padding: ($layout-spacer-y * 1) ($layout-spacer-x * 1) ($layout-spacer-y * .5)

    &__title
      font-weight: $font-weight-bold
      font-size: $h4-font-size

  &__main
    height: 100%
    display: flex
    flex-direction: column

  &__table-scroll
    @include always-show-scrollbar()
    contain: size
    overflow-x: scroll
    height: 100%
    flex-grow: 1
    scroll-padding-bottom: $cumul-row-height
    container-type: inline-size

  //////////////////////////////////////

  &__filters

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

  //////////////////////////////////////

  &__tab-menu
    // (empirical: vertical visual alignment with above filter container)
    $padding-left: 18px

    padding-left: $padding-left

    :deep()

      .layout-nav-tab-menu
        @include tab-menu-compact


  ///////////////////////////////////////

  &__row-selection

    :deep(.ui-table-top-row-selection__inner)
      width: 100cqw

  ///////////////////////////////////////

  &__empty
    position: sticky
    left: 0
    width: 100cqw
    display: flex
    flex-direction: column
    align-items: center
    margin: 20vh 0 space-x(2)

  ///////////////////////////////////////

  &__footer
    border-top: 1px solid $border-color
    display: flex
    align-items: center
    justify-content: space-between
    padding: ($layout-spacer-y * .25) ($layout-spacer-x * .5)

    &__count
      font-size: 85%

    &__per-page
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0

      &__text
        font-weight: $font-weight-light
        font-size: 90%
        white-space: nowrap

  ///////////////////////////////

  :deep(.b-table.table)
    @include table-presets
    @include table-body-grow

    margin-bottom: 0
    height: 100%

    /// Header chrome, needed on both nodes:
    ///   - `thead`: outlives the `hide-header` state (see <ui-table-wrapper>,
    ///     `visibility` suppressing the element own painting)
    ///   - `th`: travels with the sticky pinning (see below)
    $header-background: $body-bg
    $header-rule: inset 0 -1px 0 $submenu-border-color

    thead
      background-color: $header-background
      box-shadow: $header-rule

    @include table-header-small
      background-color: $header-background
      box-shadow: $header-rule
      font-size: 13.5px

    #{ $self }__cumul-row__label
      font-weight: $font-weight-bold
      letter-spacing: 0.5px

    #{ $self }__selection
      width: $selection-column-width
      text-align: center

    #{ $self }__actions
      width: $actions-column-width

    #{ $self }__code-societe
      width: $code-societe-column-width

    #{ $self }__numero-paie
      width: $numero-paie-column-width
      white-space: nowrap

    #{ $self }__profession
      width: $profession-column-width

    #{ $self }__salarie
      max-width: 100%
      width: $salarie-column-width

    #{ $self }__date
      width: $date-column-width

    #{ $self }__date_reglement
      width: $date-reglement-column-width

    #{ $self }__heures
      width: $heures-column-width
      text-align: right

    #{ $self }__brut
      white-space: nowrap
      width: $brut-column-width
      text-align: right

    #{ $self }__net-a-payer
      width: $net-a-payer-column-width
      white-space: nowrap
      text-align: right

    #{ $self }__net-avant-pas
      width: $net-avant-pas-column-width
      white-space: nowrap
      text-align: right

    #{ $self }__cout-employeur
      width: $cout-employeur-column-width
      text-align: right

    #{ $self }__jours
      width: $jours-column-width
      text-align: right

    #{ $self }__cachets
      width: $cachets-column-width
      text-align: right

    #{ $self }__type-contrat
      width: $type-contrat-column-width

    #{ $self }__code-contrat
      width: $code-contrat-column-width

    #{ $self }__contrat-badge
      font-size: 95%
      @include paie-type-badge()

    #{ $self }__date-dsn
      width: $date-dsn-column-width

    #{ $self }__reglement
      width: $reglement-column-width

    #{ $self }__analytique-libelle
      width: $analytique-libelle-column-width

    #{ $self }__analytique-code
      width: $analytique-code-column-width

    #{ $self }__numero-objet
      width: $numero-objet-column-width

    #{ $self }__etat
      width: $etat-column-width

      &__container
        display: flex
        align-items: center
        justify-content: flex-start
        gap: space-x(0.5)
        font-size: 140%

        &__svg
          width: $icon-default-size
          height: $icon-default-size

    #{ $self }__derniere-modif
      width: $derniere-modif-column-width
      text-align: center

  ////////////////////////////////////

  /// Sticky/fixed blocks
  ///
  /// (note that "boostrap-vue-next" ":sticky-header" prop is used to make
  /// the header sticky via an additional inner scroll → we already have such
  /// a container, see "__table-scroll", hence the ad hoc override)

  :deep(.b-table.table)

    thead th
      padding-top: initial
      padding-bottom: space-x(1)
      position: sticky
      top: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot
      background-color: map-get($palette-layout, 'color-3')
      position: sticky
      bottom: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot tr.paies-list-page__cumul-row td
      gap: 0
      color: $white
      font-weight: $font-weight-normal
      overflow: visible
      text-align: right
      font-size: 14px
      transition: opacity 200ms ease

    tfoot tr.paies-list-page__cumul-row--loading td:not(:first-child)
      opacity: 0.5

  ////////////////////////////////////

  &__preview-loader
    display: flex
    align-items: center
    justify-content: center
    padding: space-x(4) 0

  ////////////////////////////////////

  /// Animation
  ///

  &__filters
    animation: fade-in 150ms ease-in-out

  &__empty
    animation: fade-in 50ms ease-in

  /// Grace delay before showing the loading rows: a fast enough consumer
  /// unmounts them before they ever get visible (avoiding a flicker whenever
  /// the result set is systematically empty)
  &__placeholder
    opacity: 0
    animation: fade-in 300ms ease-in 150ms forwards

</style>

<script lang="ts">
import PaiesListFilters from '~/components/paies/list/Filters.vue'
import SinglePaieViewer from '~/components/paies/preview/SinglePaieViewer.vue'
import { useCompanyOptionalNavigationContext } from '~/composables/layout/navigation'
import type { UiTableWrapper } from '#components'
import type { ComponentExposed } from 'vue-component-type-helpers'
import { useTableProvider, type DefaultData as ProviderDefaultData, type Config as TableConfig } from '~/composables/layout/table-provider'
import {
  PaieStatusFilter,
  SpecificitePaie,
  printTypeContrat,
  printDraftNumeroPaie,
  TAB_MENU,
  TAB_MENU_TO_STATUS_FILTER,
  useStore_paies_list,
  getDefaultFilters,
  type PaieCumuls,
  type Paie,
  type PaiesListFilters as $PaiesListFilters
} from '~/stores/paies/list'
import { useStore_paies_pdf } from '~/stores/paies/pdf'
import { useToastBuilder } from '~/composables/layout/toast'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { any, equals, notEmpty, omit, or, prop, map, Maybe, toString, pipe_, tapCatch, findIndex, propEq, clone, lensIndex, over, assoc, singleton, pluck, values } from '~/utils/lib/core/prelude'
import { parseDatetime } from '~/utils/lib/core/datetime'
import { toString as floatToString } from '~/utils/lib/core/float'
import type { LayoutNavTabMenuPropsItem } from '~/components/layout/nav/TabMenu.vue'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import { useTableSelectionMultipleHolistic } from '~/composables/layout/table-selection-multiple-holistic'
import Console from '~/utils/lib/core/console'
import { getPaieDetailsRoute } from '~/stores/paies/routes'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useAccessControl } from '~/composables/layout/access-control'
import { STATUS } from '~/utils/lib/layout/ui'
import type { TableField } from '~/components/ui/table/Wrapper.vue'
import { useStore_account_details } from '~/stores/account/details'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useIncrementalToken } from '~/composables/core/incremental-token'
import { useStore_paies_list_salarie } from '~/stores/paies/list/salarie'

const console = new Console('page:paies-list')

function formatToInteger(value): string {
  if (value === undefined) return ''
  return floatToString(Number(value), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function formatToDecimal(value): string {
  if (value === undefined) return ''
  return floatToString(Number(value), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const tabMenuItems_boilerplate :LayoutNavTabMenuPropsItem[] = [
  { id: TAB_MENU.ALL, text: 'Toutes' },
  { id: TAB_MENU.DRAFT, text: 'Brouillon' },
  { id: TAB_MENU.CALCULATED, text: 'Calculé' },
]

function formatDate(date): string {
  if (!date) return ''
  return pipe_(
    date,
    parseDatetime({
      inputFormat: undefined,
      outputFormat: 'DD/MM/YY',
    }),
  )
}

function formatNumeroPaie({ value, item }): string {
  if (equals(item?.status_paie, PaieStatusFilter.CALCULATED))
    return String(value ?? '')
  if (equals(item?.status_paie, PaieStatusFilter.DRAFTED))
    return `(${printDraftNumeroPaie(value)})`
  return ''
}

function formatSocieteCode({ item }): string {
  return item?.societe?.code_societe ?? ''
}

function formatAnalytiquesLibelle({ item }): string {
  return item?.analytiques?.map(a => a.libelle).join(', ') ?? ''
}

function formatAnalytiquesCode({ item }): string {
  return item?.analytiques?.map(a => a.id).join(', ') ?? ''
}

function formatNumeroObjet({ item }): string {
  return item?.numero_objet?.libelle ?? ''
}

function formatProfession({ item }): string {
  return item?.profession?.libelle ?? ''
}

function formatCodeContrat({ item }): string {
  return item?.contrat?.code ?? ''
}

function formatDatesDsn({ value }): string {
  if (!value?.length) return ''
  return value.map(date => formatDate(date)).join(', ')
}

export default defineNuxtComponent({
  components: {
    PaiesListFilters,
    SinglePaieViewer,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.PAIES,
    })

    const navigationContext = useCompanyOptionalNavigationContext()

    useHead(() => {
      const currentLevel = navigationContext.fromCurrentLevel()

      switch (currentLevel.kind) {
        case 'COMPANY':
          return {
            title: `Paies — ${currentLevel.company.raison_sociale}`,
          }
        case 'STRUCTURE':
          return {
            title: `Paies — ${currentLevel.structure.raison}`,
          }
        default:
          return {
            title: 'Paies',
          }
      }
    })

    const ref_table = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')
    const store_paies_list = useStore_paies_list()
    const store_paies_pdf = useStore_paies_pdf()
    const store_salaries = useStore_paies_list_salarie()
    const ToastBuilder = useToastBuilder()
    const store_account_details = useStore_account_details()

    const providerDefaultData :ProviderDefaultData<Paie, $PaiesListFilters> = {
      context: {
        perPage: 100,
        order: 'numero_paie',
        sort: 'desc',
        filters: getDefaultFilters(),
      },
    }
    const tableConfig :TableConfig = {
      precedingSorting: {
        sort: 'desc',
        order: 'numero_paie',
      },
    }
    const tableProvider = useTableProvider<Paie, $PaiesListFilters>(
      ref_table,
      providerDefaultData,
      tableConfig
    )

    const tableSelectionMultiple = useTableSelectionMultipleHolistic(
      tableProvider.currentItems,
      (item, item_) => {
        const id = prop('id', item)
        const id_ = prop('id', item_)
        return equals(id, id_)
      },
      tableProvider.context
    )

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const current_societe_ids = (() => {
      const currentLevel = navigationContext.fromCurrentLevel()

      switch (currentLevel.kind) {

        case 'COMPANY':
          return singleton(currentLevel.company.societeId )

        case 'STRUCTURE':
          return pipe_(
            values(currentLevel.structure.societes),
            pluck('societeId')
          )
      }
    })()

    const hasAnyCriterionSet = computed(() => {
      const filters_ = omit([ 'dates_reglement' ], tableProvider.context.value.filters)

      return or(
        any(notEmpty, values(filters_ as unknown as object)),
        notEmpty(tableProvider.context.value.search)
      )
    })

    return {
      fetchState,
      fetchWrapper,
      ...navigationContext,
      ...tableProvider,
      hasAnyCriterionSet,
      ...tableSelectionMultiple,
      ...useLinkHandler(),
      ...useAccessControl(),
      store_account_details,
      store_paies_list,
      store_salaries,
      store_paies_pdf,
      ToastBuilder,
      ref_table,
      TAB_MENU,
      PaieStatusFilter,
      SpecificitePaie,
      printTypeContrat,
      current_societe_ids,
      previewToken: useIncrementalToken(),
      cumulsToken: useIncrementalToken(),
    }
  },
  data() {
    return {
      tabMenuItems: clone(tabMenuItems_boilerplate),
      selectedTabMenu: TAB_MENU.ALL,
      cumuls: Maybe.Nothing<PaieCumuls>(),
      isCumulsLoading: false,
      displayPreviewSidebar: false,
      previewPdfUrl: '',
      previewFilename: '',
      isPreviewLoading: false,
      fields: [
        {
          key: 'selection',
          label: '',
          class: 'paies-list-page__selection',
        },
        {
          key: 'code_societe',
          label: 'Code soc.',
          class: 'paies-list-page__code-societe',
          formatter: formatSocieteCode,
          tooltip: formatSocieteCode,
          truncate: true,
          manageable: true,
          displayed: true,
        },
        {
          key: 'numero_paie',
          label: 'N° paie',
          tdClass: 'font-weight-bold',
          class: 'paies-list-page__numero-paie',
          formatter: formatNumeroPaie,
          sortable: true,
          displayed: true,
          pinned: 'left',
          manageable: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'type_contrat',
          label: 'Type',
          class: 'paies-list-page__type-contrat',
          manageable: true,
          displayed: true,
        },
        {
          key: 'nom',
          label: 'Nom salarié',
          class: 'paies-list-page__salarie',
          sortable: true,
          manageable: true,
          displayed: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'date_debut',
          label: 'Du',
          class: 'paies-list-page__date',
          formatter: ({ value }) => formatDate(value),
          manageable: true,
          displayed: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'date_fin',
          label: 'Au',
          class: 'paies-list-page__date',
          formatter: ({ value }) => formatDate(value),
          manageable: true,
          displayed: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'heures',
          label: 'Heures',
          class: 'paies-list-page__heures',
          formatter: ({ value }) => formatToInteger(value),
          manageable: true,
          displayed: true,
        },
        {
          key: 'brut',
          label: 'Brut',
          class: 'paies-list-page__brut',
          formatter: ({ value }) => formatToDecimal(value),
          sortable: true,
          manageable: true,
          displayed: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'net_a_payer',
          label: 'Net à payer',
          class: 'paies-list-page__net-a-payer',
          formatter: ({ value }) => formatToDecimal(value),
          sortable: true,
          manageable: true,
          displayed: true,
        },
        {
          key: 'net_avant_pas',
          label: 'Net av. PAS',
          class: 'paies-list-page__net-avant-pas',
          formatter: ({ value }) => formatToDecimal(value),
          sortable: true,
          manageable: true,
          displayed: false,
        },
        {
          key: 'cout_employeur',
          label: 'Coût employeur',
          class: 'paies-list-page__cout-employeur',
          formatter: ({ value }) => formatToDecimal(value),
          sortable: true,
          manageable: true,
          displayed: false,
        },
        {
          key: 'jours',
          label: 'Jours',
          class: 'paies-list-page__jours',
          formatter: ({ value }) => formatToInteger(value),
          manageable: true,
          displayed: false,
        },
        {
          key: 'cachets',
          label: 'Cachets',
          class: 'paies-list-page__cachets',
          formatter: ({ value }) => formatToInteger(value),
          manageable: true,
          displayed: false,
        },
        {
          key: 'dates_dsn',
          label: 'Date DSN',
          class: 'paies-list-page__date-dsn',
          formatter: formatDatesDsn,
          manageable: true,
          displayed: true,
        },
        {
          key: 'date_reglement',
          label: 'Date Règlt',
          class: 'paies-list-page__date_reglement',
          formatter: ({ value }) => formatDate(value),
          manageable: true,
          displayed: true,
        },
        {
          key: 'analytiques_libelle',
          label: 'Analytique lib.',
          class: 'paies-list-page__analytique-libelle',
          formatter: formatAnalytiquesLibelle,
          tooltip: formatAnalytiquesLibelle,
          truncate: true,
          manageable: true,
          displayed: false,
        },
        {
          key: 'analytiques_code',
          label: 'Analytique code',
          class: 'paies-list-page__analytique-code',
          formatter: formatAnalytiquesCode,
          tooltip: formatAnalytiquesCode,
          truncate: true,
          manageable: true,
          displayed: false,
        },
        {
          key: 'numero_objet',
          label: 'N° objet',
          class: 'paies-list-page__numero-objet',
          formatter: formatNumeroObjet,
          tooltip: formatNumeroObjet,
          truncate: true,
          manageable: true,
          displayed: false,
        },
        {
          key: 'code_contrat',
          label: 'Contrat',
          class: 'paies-list-page__code-contrat',
          formatter: formatCodeContrat,
          manageable: true,
          displayed: true,
        },
        {
          key: 'profession',
          label: 'Professions',
          class: 'paies-list-page__profession',
          formatter: formatProfession,
          tooltip: formatProfession,
          truncate: true,
          manageable: true,
          displayed: true,
        },
        {
          key: 'etat',
          label: 'Etat',
          class: 'paies-list-page__etat',
          manageable: true,
          displayed: true,
          manageStatus: STATUS.DISABLED,
        },
        {
          key: 'derniere_modif',
          label: 'Dernière modif.',
          class: 'paies-list-page__derniere-modif',
          formatter: ({ value }) => formatDate(value),
          manageable: true,
          displayed: true,
        },
        /// @NOTE: not used for now
        // {
        //   key: 'actions',
        //   label: '',
        //   class: 'paies-list-page__actions',
        // },
      ] as TableField[],
      perPageOptions: [
        { value: 100, text: '100' },
        { value: 500, text: '500' },
        { value: 1000, text: '1000' },
      ],
    }
  },
  computed: {
    isCumulVisible(): boolean {
      return this.hasAnyItemSelected
    },
    selectionCount(): number {
      return this.selectionType === 'all'
        ? this.context.total
        : this.bufferedSelectedItemsCount
    },
    canPreview(): boolean {
      return this.selectionType !== 'all'
        && this.bufferedSelectedItemsCount === 1
    },
  },
  watch: {
    selectedTabMenu() {
      this.resetSelection()

      switch(true) {
        case equals(this.context.currentPage, 1):
          this.refreshTable()
          break

        default:
          this.context.currentPage = 1
          break
      }
    },
    'context.filters'() {
      this.resetSelection()
    },
    'context.currentPage'() {
      this.resetSelection()
    },
    'context.total'() {
      const index = findIndex(
        propEq('id', this.selectedTabMenu),
        tabMenuItems_boilerplate
      )

      this.tabMenuItems
        = over(
          lensIndex(index),
          assoc('badgeCount', this.context.total),
          tabMenuItems_boilerplate
        )
    },
    fields: {
      deep: true,
      handler() {
        if (this.fetchState.status.value === 'pending')
          return

        this.savePreferences()
      },
    },
  },
  created() {
    this.fetchWrapper(
      () => this.loadPreferences()
    )
    this.consumer = (context) => {
      const { structure: { uuidData } } = this.fromCurrentLevel()

      const input = wrapInput({
        ...context,
        filters: {
          ...context.filters,
          societe_ids: this.current_societe_ids,
          status_paie: TAB_MENU_TO_STATUS_FILTER[this.selectedTabMenu],
        },
      })

      return this.store_paies_list.list({ uuidData, input })
        .catch(tapCatch(err => {
          console.warn('list', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la récupération des paies')
            .show()
        }))
    }
    this.resetFilters()
  },
  mounted() {
    this.$watch(
      instance => [
        instance.selectionType,
        instance.bufferedSelectedItems
      ],
      () => this.refreshCumuls()
    )
  },
  beforeUnmount() {
    this.previewToken.inc()
    this.revokePreviewUrl()
  },
  methods: {
    resetFilters() {
      this.store_salaries.$patch({ selectedSalaries: []})
      this.resetCriterion()
    },
    loadPreferences() {
      const { structure: { uuidData } } = this.fromCurrentLevel()
      const options = {
        uuidData,
        id: this.store_account_details.id,
        fields: clone(this.fields) as TableField[]
      }

      return this.store_paies_list.loadPreferences(options)
        .then((fields) => { this.fields = fields })
        .catch(tapCatch((err) => {
          console.warn('loadPreferences', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la récupération de l\'affichage des colonnes')
            .show()
        }))
    },
    savePreferences() {
      const { structure: { uuidData } } = this.fromCurrentLevel()

      return this.store_paies_list.savePreferences({
          uuidData,
          id: this.store_account_details.id,
          fields: clone(this.fields) as TableField[]
        })
        .catch((err) => {
          console.warn('savePreferences', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de l\'enregistrement de l\'affichage des colonnes')
            .show()
        })
    },
    loadSalarieOptions(search: string) {
      const { structure: { uuidData } } = this.fromCurrentLevel()
      const options = {
        uuidData,
        search,
        societe_ids: this.current_societe_ids,
      }

      return this.store_salaries.search(options)
        .catch((err) => {
          console.warn('loadSalarieOptions', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la recherche des salariés')
            .show()
          return []
        })
    },
    searchProfessions(search: string) {
      const { structure: { uuidData } } = this.fromCurrentLevel()

      // (KISS: reusing a list API)
      const pagination = {
        current_page: 1,
        per_page: 10,
      }
      const filters = {
        societe_ids: this.current_societe_ids,
        search,
      }
      const options = {
        uuidData,
        filters,
        pagination,
      }

      return this.store_paies_list.searchProfessions(options)
        .catch((err) => {
          console.warn('searchProfessions', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la recherche des professions')
            .show()
          return []
        })
    },
    formatCumul(key: keyof PaieCumuls): string {
      return Maybe.maybe(
        '',
        (cumuls: PaieCumuls) => {
          switch (key) {
            case 'heures':
            case 'jours':
            case 'cachets':
              return formatToInteger(cumuls[key])
            default:
              return formatToDecimal(cumuls[key])
          }
        },
        this.cumuls
      )
    },
    openPaieDetails(item: import('~/stores/paies/list').Paie) {
      if (!this.hasFeature(this.FEATURES.FLA_1_DETAILS_PAIE)) return

      const currentLevel = this.fromCurrentLevel()

      switch (currentLevel.kind) {
        case 'STRUCTURE': {
          const route = getPaieDetailsRoute(
            currentLevel.structure.id,
            item.societe.id,
            item.id,
          )
          return this.goToRoute(route)
        }
        case 'COMPANY': {
          const route = getPaieDetailsRoute(
            currentLevel.structure.id,
            item.societe.id,
            item.id,
            currentLevel.company.code_societe,
          )
          return this.goToRoute(route)
        }
      }
    },
    refreshCumuls() {
      switch (this.hasAnyItemSelected) {

        case true:
          this.fetchCumuls()
          break

        case false:
          this.cumulsToken.inc()
          this.cumuls = Maybe.Nothing()
          this.isCumulsLoading = false
          break

      }
    },
    openPreview() {
      if (!this.canPreview)
        return

      const paie = this.bufferedSelectedItems[0]
      if (!paie)
        return

      const { structure: { uuidData } } = this.fromCurrentLevel()

      this.revokePreviewUrl()
      this.previewFilename = `paie-${paie.numero_paie}.pdf`
      this.isPreviewLoading = true
      this.displayPreviewSidebar = true

      const token = this.previewToken.inc()

      return this.store_paies_pdf.exec({
        uuidData,
        societe_id: paie.societe.id,
        paie_id: paie.id,
      })
        .then((url: string) => {
          if (token !== this.previewToken.current) {
            URL.revokeObjectURL(url)
            return
          }
          this.previewPdfUrl = url
        })
        .catch((err) => {
          if (token !== this.previewToken.current)
            return

          this.displayPreviewSidebar = false
          console.warn('openPreview', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la prévisualisation de la paie')
            .show()
        })
        .finally(() => {
          if (token !== this.previewToken.current)
            return

          this.isPreviewLoading = false
        })
    },
    onClosePreview() {
      this.previewToken.inc()
      this.revokePreviewUrl()
      this.previewFilename = ''
      this.isPreviewLoading = false
    },
    revokePreviewUrl() {
      if (!this.previewPdfUrl)
        return
      URL.revokeObjectURL(this.previewPdfUrl)
      this.previewPdfUrl = ''
    },
    fetchCumuls() {
      const { structure: { uuidData } } = this.fromCurrentLevel()

      const paie_ids = this.selectionType === 'all' ?
        undefined :
        map.iterable(
          prop('id'),
          this.bufferedSelectedItems,
        )

      const filters = {
        ...this.context.filters,
        paie_ids,
        societe_ids: this.current_societe_ids,
        status_paie: TAB_MENU_TO_STATUS_FILTER[this.selectedTabMenu],
      }

      const token = this.cumulsToken.inc()
      this.isCumulsLoading = true

      return this.store_paies_list.cumuls({ uuidData, filters })
        .then((cumuls: PaieCumuls) => {
          if (token !== this.cumulsToken.current)
            return

          this.cumuls = Maybe.Just(cumuls)
        })
        .catch((err) => {
          if (token !== this.cumulsToken.current)
            return

          console.warn('fetchCumuls', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors du calcul des cumuls')
            .show()
        })
        .finally(() => {
          if (token !== this.cumulsToken.current)
            return

          this.isCumulsLoading = false
        })
    },
  },
})
</script>
